import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, FormInstance, SelectPicker, Checkbox, InputGroup, Modal, Message, Button, ModalProps, Textarea} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel} from '@/models/core.model';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import FetchingSelect from '@/components/FetchingSelect';
import ImageUploader from '@/components/ImageUploader';
import { getCitiesLookup, getCity } from '@/services/city.service';
import { getCountriesLookup } from '@/services/country.service';
import ZonedDatePicker from '@/components/ZonedDatePicker';
import { City } from '@/models/city.model';
import { getRunEventKeysLookup } from '@/services/run-event-key.service';
import { Icon } from '@rsuite/icons';
import { MdContentCopy, MdLink } from 'react-icons/md';
import { ApproveScrapedMarathon, ApproveScrapedDistance, ScrapedEvent } from '@/models/scraped-event.model';
import { RunEventDistanceTypeList } from '@/models/run-event-distance.model';
import MapToSelectPath from '@/components/MapToSelectPath';
import { utc_iana_time_zone_id } from '@/constants/time';
import { approveScrapedEvent } from '@/services/scraped-event.service';
import EventLinksEditor from '@/components/EventLinksEditor';
import { cleanEventLinks } from '@/models/event-link.model';
import ScrapedDistancesEditor, { ScrapedDistanceRow, defaultRouteIndex, rowsFromScraped, rowsToRequest } from './scraped-distances-editor';

const marathonSchema = Schema.Model<ApproveScrapedMarathon>({
  name: Schema.Types.StringType().isRequired('This field is required.'),
  website: Schema.Types.StringType(),
  imageUrl: Schema.Types.StringType(),
  cityId: Schema.Types.NumberType().isRequired('This field is required.'),
  countryId: Schema.Types.NumberType(),
  registrationDate: Schema.Types.DateType(),
  marathonKeyId: Schema.Types.NumberType(),
  isMajorMarathon: Schema.Types.BooleanType().isRequired('This field is required.'),
  path: Schema.Types.ArrayType(),
  marathonDistanceType: Schema.Types.NumberType().when(schemaSpec => {
    const path = schemaSpec.path.value as Array<any> | undefined;
    if (path && path?.length > 0) {
        return Schema.Types.NumberType().isRequired('This field is required when route information is available.');
    }
    return Schema.Types.NumberType();
  }),
  startDate: Schema.Types.DateType().when(schemaSpec => {
    const path = schemaSpec.path.value as Array<any> | undefined;
    if (path && path?.length > 0) {
        return Schema.Types.DateType().isRequired('This field is required when route information is available.');
    }
    return Schema.Types.DateType();
  }),
});


const emptyModel: ApproveScrapedMarathon = {
  name: undefined,
  cityId: undefined,
  countryId: undefined,
  imageUrl: undefined,
  website: undefined,
  marathonKeyId: undefined,
  registrationDate: undefined,
  isMajorMarathon: false,
  startDate: undefined,
  marathonDistanceType: undefined,
  path: undefined,
};

interface ApproveScrapedMarathonModalProps extends ModalProps{
  formObject?: ScrapedEvent;
};

const ApproveScrapedMarathonModal: React.FC<ApproveScrapedMarathonModalProps> = ({formObject, open, onClose}) => {
  const [formValue, setFormValue] = useState<ApproveScrapedMarathon>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState< City | undefined>(undefined);
  const [hasDistance, setHasDistance] = useState<boolean>(false);
  // Scraped races (multi-distance events). When present they replace the single distance/route fields.
  const [raceRows, setRaceRows] = useState<ScrapedDistanceRow[]>([]);
  const [routeIndex, setRouteIndex] = useState<number | undefined>(undefined);

  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);
    
    useEffect(() => {
        if(formObject){
            const rows = rowsFromScraped(formObject.distances, true);
            setRaceRows(rows);
            setRouteIndex(defaultRouteIndex(rows, formObject.path));
            const anyPath = rows.length === 0 && formObject.path && formObject.path.length > 0;
            setFormValue(pre => ({...pre, 
                name: formObject?.name,
                description: formObject?.description,
                imageUrl: formObject?.imageUrl,
                registrationDate: formObject.registrationDate,
                countryId: formObject.countryId,
                cityId: formObject.cityId,
                website: formObject.sourceWebsiteUrl,
                links: formObject.links ?? [],
                path: anyPath ? formObject.path : undefined,
                startDate: anyPath ? formObject.startDate : undefined
            }));
            if(formObject.cityId){
                updateSelectedCity(formObject.cityId);
            }
            setHasDistance(formObject.path && formObject.path.length > 0);
        }else {
            setFormValue(emptyModel);
            setHasDistance(false);
        }

    }, [formObject]);
    
    const handleSubmit = async () => {
    if(!formObject || !formObject.eventType) return;

    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    let distances: ApproveScrapedDistance[] | undefined = undefined;
    if(raceRows.length > 0){
      const res = rowsToRequest(raceRows, routeIndex, formObject.path);
      if(typeof res === 'string'){
        setErrorModel({ success: false, errorMessage: res, errorCode: 0 });
        return;
      }
      distances = res;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result = await approveScrapedEvent({
        scrapedEventId: formObject?.id,
        eventType: formObject.eventType,
        marathon: {...formValue, distances, links: cleanEventLinks(formValue.links)}
      });
      if (result.success) {
        nav('/run-events/' + result.result);
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const updateSelectedCity = async(cityId: number|undefined) => {
    if(!cityId){
      setSelectedCity(undefined);
      return;
    }

    const city = (await getCity(cityId)).result;
    setSelectedCity(city);
    setFormValue(pre => ({...pre, countryId: city?.country?.id}));
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };
  
  const handleCountryChange = () => {
    setFormValue(pre => ({ ...pre, cityId: undefined, marathonKeyId: undefined }));
    setSelectedCity(undefined);
  };

  const handleCityChange = (value) => {
    setFormValue(pre => ({ ...pre, cityId: value, marathonKeyId: undefined}));
    updateSelectedCity(value);
  };

  const handleModalClose = (event?: React.SyntheticEvent<Element, Event> | undefined) => {
    setFormValue(emptyModel);
    setErrorModel(undefined);
    setSelectedCity(undefined);
    setRaceRows([]);
    setRouteIndex(undefined);
    setLoading(false);
    if(onClose){
      onClose(event);
    }
  };

  return (
    <Modal open={open} onClose={handleModalClose} size="lg" backdrop={'static'}>
        <Modal.Header>
            <Modal.Title>Approve Scraped Marathon</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form name='approve-marathon-form' fluid 
                ref={formRef}
                onChange={handleFormChange}
                onCheck={setFormError}
                formValue={formValue}
                model={marathonSchema}
                disabled={loading}
            >
            <Grid w='100%' fluid>
                <Row>
                    <Col span={{xs:24}} >
                        <Form.Label>Image</Form.Label>              
                        <Form.Control
                            accepter={ImageUploader}
                            name="imageUrl"
                            height="300px"
                            url={formValue.imageUrl}
                            objectStorageCategory={ObjectStorageFileCategory.Marathon}
                            clearable={false}
                            uploadable={false}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col span={{xs:24}} >
                    <Form.Group>
                        <Form.Label>Name </Form.Label>
                        <Form.Control name="name" value={formValue.name ?? ''}/>
                    </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col span={{xs:24}} >
                    <Form.Group>
                        <Form.Label>Description</Form.Label>
                        <Form.Control name="description" accepter={Textarea} rows={3}/>
                    </Form.Group>
                    </Col>
                </Row>                
                <Row>
                    <Col span={{xs:24, xl:12}} >
                        <Form.Group controlId='countryId'>
                            <Form.Label>Country</Form.Label>
                            <Form.Control name="countryId" accepter={FetchingSelect} block
                            fetchData={(text, page) => getCountriesLookup(text, page)} 
                            placeholder="Select a country"
                            onChange={handleCountryChange}
                            /> 
                        </Form.Group>
                    </Col>
                    <Col span={{xs:24, xl:12}} >
                        <Form.Group controlId='cityId'>
                            <Form.Label>City</Form.Label>
                            <Form.Control name="cityId" accepter={FetchingSelect} block
                            fetchData={(text, page) => getCitiesLookup(text, formValue?.countryId, page)} 
                            placeholder="Select a city"
                            onChange={handleCityChange}
                            reFetchOnOpen = {true}
                            /> 
                        </Form.Group>
                    </Col>
                </Row>               
                <Row>
                    <Col span={{xs:24, xl:12}}>
                        <Form.Group>
                            <Form.Label>Registration Date</Form.Label>
                            <Form.Control accepter={ZonedDatePicker} block
                            name="registrationDate" 
                            value={formValue.registrationDate ?? null}
                            onChange={dt => setFormValue(pre => ({...pre, registrationDate: dt ?? undefined}))}
                            zoneIanaId={selectedCity?.ianaTimeZoneId ?? utc_iana_time_zone_id}
                            />
                        </Form.Group>                          
                    </Col>
                    <Col span={{xs:24, xl:12}}>
                        <Form.Group>
                            <Form.Label>Key</Form.Label>
                            <Form.Control name="marathonKeyId" accepter={FetchingSelect} block
                            fetchData={(text, page) =>getRunEventKeysLookup(text, formValue.cityId, page)} 
                            placeholder="Select a key"
                            disabled={!formValue.cityId}
                            reFetchOnOpen = {true}
                            /> 
                        </Form.Group>
                    </Col>
                </Row> 
                <Row>
                    <Col span={{xs:24, xl:20}}>
                        <Form.Group>
                            <Form.Label>Website</Form.Label>
                            <InputGroup inside>
                            <InputGroup.Button onClick={() => formValue?.website && window.open(formValue?.website, '_blank') }>
                                <Icon as={MdLink} />
                            </InputGroup.Button>
                            <Form.Control name="website" />
                            <InputGroup.Button onClick={() => navigator.clipboard.writeText(formValue?.website || '')}>
                                <Icon as={MdContentCopy} />
                            </InputGroup.Button>
                            </InputGroup>                     
                        </Form.Group>                 
                    </Col>
                    <Col span={{xs:24, xl:4}} >
                        <Form.Group>
                            <Form.Label>Is Major</Form.Label>
                            <Form.Control name="isMajorMarathon" accepter={Checkbox} 
                            checked={!!formValue.isMajorMarathon} 
                            onChange={(val, chk) => setFormValue(pre => ({ ...pre, isMajorMarathon: chk }))}
                            />                  
                        </Form.Group>                   
                    </Col>                    
                </Row>
                <Row>
                    <Col span={{xs:24}}>
                        <Form.Group>
                            <Form.Label>Links</Form.Label>
                            <EventLinksEditor value={formValue.links} disabled={loading}
                                onChange={links => setFormValue(pre => ({...pre, links}))} />
                        </Form.Group>
                    </Col>
                </Row>
                { raceRows.length > 0 && <>
                    <ScrapedDistancesEditor
                        rows={raceRows}
                        onChange={setRaceRows}
                        routeIndex={routeIndex}
                        onRouteIndexChange={setRouteIndex}
                        hasRoute={!!formObject?.path && formObject.path.length > 0}
                        showType={true}
                        zoneIanaId={selectedCity?.ianaTimeZoneId}
                        disabled={loading}
                    />
                    {formObject?.path && formObject.path.length > 0 && <Row>
                        <Col span={{xs:24}}>
                        <MapToSelectPath
                            value={formObject?.path}
                            height={`400px`}
                            zoom={13}
                            disabled
                            options= {{scrollwheel: false}}
                        />
                        </Col>
                    </Row>}
                </>}
                { raceRows.length === 0 && !hasDistance && <Message showIcon type="warning">
                    The Scraped Marathon does not have route information. You can approve it as a Marathon Event without distance details, but users will not be able to track runs for this event until distance and route are added.
                </Message>}
                { raceRows.length === 0 && hasDistance && <>
                    <Row>
                        <Col span={{xs:24, xl:12}}>
                            <Form.Group>
                            <Form.Label>Marathon Type</Form.Label>
                            <Form.Control name="marathonDistanceType" accepter={SelectPicker} block
                                data={RunEventDistanceTypeList} 
                                searchable={false}
                                placeholder="Select a type"
                            /> 
                            </Form.Group>                  
                        </Col>
                        <Col span={{xs:24, xl:12}}>
                            <Form.Group>
                            <Form.Label>Start Date</Form.Label>
                                <Form.Control accepter={ZonedDatePicker} block
                                name="startDate" 
                                value={formValue.startDate ?? null}
                                onChange={dt => setFormValue(pre => ({...pre, startDate: dt ?? undefined}))}
                                zoneIanaId={selectedCity?.ianaTimeZoneId ?? utc_iana_time_zone_id}
                                />                  
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={{xs:24}}>
                        <MapToSelectPath
                            value={formObject?.path}
                            height={`400px`}
                            zoom={13}
                            disabled
                            options= {{scrollwheel: false}}
                        />
                        </Col>                        
                    </Row>
                </>}     
            </Grid>
            </Form>
            {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}
        </Modal.Body>
        <Modal.Footer>
            <Stack justifyContent='space-between' style={{paddingTop: 15}}>
                <Button onClick={handleModalClose} appearance="subtle" disabled={loading}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} appearance="primary" loading={loading}>
                    Approve
                </Button>
            </Stack>
        </Modal.Footer>       
    </Modal>
  );
};

export default ApproveScrapedMarathonModal;

